import { useEffect, useState, type ComponentType } from 'react';
import { apiClient } from '@/lib/axios';
import { Button, GoogleMark, MicrosoftMark } from '@/components/ui';

type ProviderId = 'google' | 'microsoft';

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

const PROVIDER_MARKS: Record<ProviderId, ComponentType<{ className?: string }>> = {
  google: GoogleMark,
  microsoft: MicrosoftMark,
};

/**
 * Renders one button per identity provider the backend reports as configured.
 *
 * The list comes from GET /auth/providers rather than a build-time flag, so a
 * provider switched off server-side stops being offered without a frontend
 * rebuild — and the two copies can never drift apart.
 */
export function SsoButtons({ returnTo = '/dashboard' }: { returnTo?: string }) {
  const [providers, setProviders] = useState<ProviderId[]>([]);
  const [pending, setPending] = useState<ProviderId | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ providers: ProviderId[] }>('/auth/providers')
      .then(({ data }) => {
        if (!cancelled) setProviders(data.providers ?? []);
      })
      // SSO being unreachable must never block the password form beneath it.
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (providers.length === 0) return null;

  const startSso = (provider: ProviderId) => {
    setPending(provider);
    // A full page navigation, not an XHR: the browser has to follow the 302 out
    // to the provider and carry the binding cookie the backend just set.
    const base = apiClient.defaults.baseURL ?? '';
    window.location.href =
      `${base}/auth/oauth/${provider}/start?returnTo=${encodeURIComponent(returnTo)}`;
  };

  return (
    <>
      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-border-subtle" />
        <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
          or continue with
        </span>
        <span className="h-px flex-1 bg-border-subtle" />
      </div>

      <div className="flex flex-col gap-2">
        {providers.map((provider) => {
          const Mark = PROVIDER_MARKS[provider];
          return (
            <Button
              key={provider}
              type="button"
              variant="secondary"
              size="md"
              fullWidth
              loading={pending === provider}
              disabled={pending !== null}
              leadingIcon={<Mark />}
              onClick={() => startSso(provider)}
            >
              Continue with {PROVIDER_LABELS[provider]}
            </Button>
          );
        })}
      </div>
    </>
  );
}
